import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Video, 
  Play,
  X,
  Save,
  Loader,
  ExternalLink
} from 'lucide-react';
import config from '../utils/config';
import Swal from 'sweetalert2';

export default function VideosAdmin() {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState({ 
    title: '', 
    description: '', 
    video_url: '', 
    thumbnail_url: '',
    category: ''
  });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');

  const categories = [
    'Events',
    'Speeches',
    'Interviews',
    'Documentary',
    'News',
    'Other'
  ];

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${config.url}/videos`);
      const data = await response.json();
      setVideos(data);
    } catch (error) {
      setError('Failed to fetch videos');
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setForm({ ...form, video_url: '' }); // Clear video_url if uploading file
    }
  };

  const clearForm = () => {
    setForm({ title: '', description: '', video_url: '', thumbnail_url: '', category: '' });
    setEditing(null);
    setVideoFile(null);
    setVideoPreview('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const url = editing 
        ? `${config.url}/videos/${editing}`
        : `${config.url}/videos`;
      let response, data;
      
      if (videoFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('title', form.title.trim());
        formData.append('description', form.description.trim());
        formData.append('thumbnail_url', form.thumbnail_url.trim());
        formData.append('category', form.category);
        formData.append('video', videoFile);
        response = await fetch(url, {
          method: editing ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Use JSON for YouTube link or existing video
        const videoData = {
          title: form.title.trim(),
          description: form.description.trim(),
          video_url: form.video_url.trim(),
          thumbnail_url: form.thumbnail_url.trim(),
          category: form.category
        };
        response = await fetch(url, {
          method: editing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(videoData)
        });
      }
      data = await response.json();
      if (response.ok) {
        setSuccess(editing ? 'Video updated successfully!' : 'Video created successfully!');
        clearForm();
        setShowForm(false);
        fetchVideos();
      } else {
        setError(data.error || 'Operation failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = item => {
    setForm({ 
      title: item.title || '', 
      description: item.description || '', 
      video_url: item.video_url || '', 
      thumbnail_url: item.thumbnail_url || '',
      category: item.category || ''
    });
    setEditing(item.id);
    if (item.video_url && item.video_url.startsWith('/uploads/')) {
      setVideoPreview(`${config.url}${item.video_url}`);
      setVideoFile(null);
    } else {
      setVideoPreview('');
      setVideoFile(null);
    }
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: 'Delete this video?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${config.url}/videos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Video deleted successfully!');
        fetchVideos();
      } else {
        setError('Failed to delete video');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const getVideoThumbnail = (videoUrl) => {
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const match = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
      const videoId = match ? match[1] : null;
      return videoId ? `https://img.youtube.com/vi/${videoId}/0.jpg` : null;
    }
    return null;
  };

  const getEmbedUrl = (videoUrl) => {
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const match = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
      const videoId = match ? match[1] : null;
      return videoId ? `https://www.youtube.com/embed/${videoId}` : videoUrl;
    }
    return videoUrl;
  };

  return (
    <div className="space-y-6 hindi-text">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Videos Management</h1>
          <p className="text-gray-600">Manage your video content and media</p>
        </div>
        <button
          onClick={() => {
            clearForm();
            setShowForm(true);
          }}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Add Video</span>
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {editing ? 'Edit Video' : 'Add New Video'}
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                clearForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Title *
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter video title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Link (or direct video link)
              </label>
              <input
                name="video_url"
                value={form.video_url}
                onChange={e => {
                  setForm({ ...form, video_url: e.target.value });
                  setVideoFile(null);
                  setVideoPreview('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter YouTube URL or video link"
                disabled={!!videoFile}
                required={!videoFile}
              />
              <p className="text-sm text-gray-500 mt-1">Supports YouTube URLs and direct video links</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or Upload Video File
              </label>
              <input
                type="file"
                accept="video/mp4,video/avi,video/mov"
                onChange={handleVideoFileChange}
                disabled={!!form.video_url}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              />
              <p className="text-sm text-gray-500 mt-1">Max size: 10MB. Only MP4, AVI, MOV allowed.</p>
            </div>
            {videoPreview && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Video Preview</label>
                <video src={videoPreview} controls className="w-full max-w-md rounded-lg shadow" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thumbnail URL
              </label>
              <input
                name="thumbnail_url"
                value={form.thumbnail_url}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter thumbnail image URL (optional)"
              />
              <p className="text-sm text-gray-500 mt-1">Leave empty to use YouTube thumbnail</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter video description"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  clearForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{editing ? 'Update' : 'Create'} Video</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map(video => {
          const thumbnail = video.thumbnail_url || getVideoThumbnail(video.video_url);
          const isUploaded = video.video_url && video.video_url.startsWith('/uploads/');
          return (
            <div key={video.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="relative">
                {isUploaded ? (
                  <video src={`${config.url}${video.video_url}`} controls className="w-full h-48 object-cover" />
                ) : thumbnail ? (
                  <img 
                    src={thumbnail} 
                    alt={video.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <Video className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => window.open(isUploaded ? `${config.url}${video.video_url}` : getEmbedUrl(video.video_url), '_blank')}
                    className="bg-white bg-opacity-90 p-3 rounded-full hover:bg-opacity-100 transition-colors"
                  >
                    <Play className="w-6 h-6 text-red-600" />
                  </button>
                </div>
                {video.category && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                    {video.category}
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">{video.title}</h3>
                {video.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{video.description}</p>
                )}
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(video)}
                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => window.open(isUploaded ? `${config.url}${video.video_url}` : getEmbedUrl(video.video_url), '_blank')}
                    className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Watch</span>
                  </button>
                  <button
                    onClick={() => handleDelete(video.id)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {videos.length === 0 && !showForm && (
        <div className="text-center py-12">
          <Video className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No videos yet</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first video</p>
          <button
            onClick={() => {
              clearForm();
              setShowForm(true);
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto"
          >
            <Plus className="w-5 h-5" />
            <span>Add Video</span>
          </button>
        </div>
      )}
    </div>
  );
} 